import {
    Component,
    defineComponent,
    h,
    onMounted,
    onUnmounted,
    PropType,
    ref,
    Ref,
} from "vue";

import LogTypes from "../../logs/exports.js";
import type { LogEntry } from "../../core/types.js";

type I18n = (key: string) => string;

type LogTypeDefinition = {
    name: string;
    tags?: readonly string[];
    filter?: {
        icon?: string;
        name?: (i18n: I18n) => string;
    };
    viewer: Component;
};

const LogTypeDefinitions = LogTypes as readonly LogTypeDefinition[];

function getLogType(log: LogEntry) {
    return LogTypeDefinitions.find((logType) => logType.name === log.type);
}

function getLogTypeLabel(log: LogEntry, logType: LogTypeDefinition | undefined, i18n: I18n) {
    return logType?.filter?.name
        ? logType.filter.name(i18n)
        : log.type;
}

function isVoiceLog(log: LogEntry) {
    return getLogType(log)?.tags?.includes("Voice") ?? false;
}

export const LogPreviewPanel = defineComponent({
    props: {
        log: {
            type: Object as PropType<LogEntry | null>,
            default: null,
        },
        members: {
            type: Object as PropType<Ref<Map<string, unknown>>>,
            required: true,
        },
        channels: {
            type: Object as PropType<Ref<Map<string, unknown>>>,
            required: true,
        },
        width: {
            type: Number,
            required: true,
        },
        fullscreen: {
            type: Boolean,
            required: true,
        },
        i18n: {
            type: Function as PropType<I18n>,
            required: true,
        },
        onresize: {
            type: Function as PropType<(event: MouseEvent) => void>,
            required: true,
        },
        onselectLog: {
            type: Function as PropType<(log: LogEntry) => void>,
            required: true,
        },
        onopenFullscreen: {
            type: Function as PropType<() => void>,
            required: true,
        },
        oncloseFullscreen: {
            type: Function as PropType<() => void>,
            required: true,
        },
        onclosePreview: {
            type: Function as PropType<() => void>,
            required: true,
        },
    },

    setup(props) {
        const isMobile = ref(false);
        let mediaQuery: MediaQueryList | null = null;

        function updateMobilePreviewMode() {
            isMobile.value = Boolean(mediaQuery?.matches);
        }

        onMounted(() => {
            mediaQuery = window.matchMedia("(max-width: 700px)");
            updateMobilePreviewMode();
            mediaQuery.addEventListener("change", updateMobilePreviewMode);
        });

        onUnmounted(() => {
            mediaQuery?.removeEventListener("change", updateMobilePreviewMode);
        });

        function closeModalPreview() {
            if (isMobile.value) {
                props.onclosePreview();
                return;
            }

            props.oncloseFullscreen();
        }

        function renderPreviewContent(log: LogEntry, logType: LogTypeDefinition | undefined, mode: "panel" | "modal") {
            if (!logType)
                return <div class="log-preview-unknown">Unknown log type.</div>;

            return h(logType.viewer, {
                log,
                members: props.members,
                channels: props.channels,
                onselectLog: props.onselectLog,
                previewMode: mode,
            });
        }

        function renderPreviewTitle(log: LogEntry, logType: LogTypeDefinition | undefined) {
            const isVoice = isVoiceLog(log);

            return (
                <div class={{ "log-preview-title": true, "log-preview-title--stacked": isVoice }}>
                    <span class="material-symbols-outlined">{isVoice ? "graphic_eq" : logType?.filter?.icon || "description"}</span>
                    <span class="log-preview-title-text">
                        <span>{isVoice ? "Log vocale" : getLogTypeLabel(log, logType, props.i18n)}</span>
                        {isVoice && <span class="log-preview-title-time">{new Date(log.createdAt).toLocaleTimeString("fr-FR")}</span>}
                    </span>
                </div>
            );
        }

        function renderPreviewShell(log: LogEntry, mode: "panel" | "modal" = "panel") {
            const logType = getLogType(log);
            const isMessagePreview = logType?.name?.startsWith("message_") === true;

            return (
                <div class={{ "log-preview-shell": true, "log-preview-shell--modal": mode === "modal" }}>
                    <header class="log-preview-header">
                        {renderPreviewTitle(log, logType)}
                        <div class="log-preview-actions">
                            {mode === "panel" && (
                                <>
                                    <button type="button" title="Agrandir" onClick={props.onopenFullscreen}>
                                        <span class="material-symbols-outlined">fullscreen</span>
                                    </button>
                                    <button type="button" title="Fermer" onClick={props.onclosePreview}>
                                        <span class="material-symbols-outlined">close</span>
                                    </button>
                                </>
                            )}
                            {mode === "modal" && (
                                <button type="button" title="Fermer" onClick={closeModalPreview}>
                                    <span class="material-symbols-outlined">close</span>
                                </button>
                            )}
                        </div>
                    </header>
                    <div class={{ "log-preview-content": true, "log-preview-content--message": isMessagePreview }}>
                        {renderPreviewContent(log, logType, mode)}
                    </div>
                </div>
            );
        }

        return () => {
            const log = props.log;

            if (!log)
                return null;

            const displayModal = props.fullscreen || isMobile.value;

            return (
                <>
                    {!isMobile.value && (
                        <>
                            <div class="log-preview-resize-handle" onMousedown={props.onresize} />
                            <aside
                                class="log-preview-container"
                                style={{ "--log-preview-width": `${props.width}px` } as Record<string, string>}
                            >
                                {renderPreviewShell(log)}
                            </aside>
                        </>
                    )}

                    {displayModal && (
                        <div class="log-preview-modal-backdrop" onClick={closeModalPreview}>
                            <div class="log-preview-modal" onClick={(event: MouseEvent) => event.stopPropagation()}>
                                {renderPreviewShell(log, "modal")}
                            </div>
                        </div>
                    )}
                </>
            );
        };
    },
});
